import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { DevTool } from "@hookform/devtools";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@material-ui/core";
import { useSnackbarsPipeline } from "@bonliva-crm/ui";
import {
  useGetConsultantOpportunityCreateLazyQuery,
  useEditConsultantOpportunityMutation
} from "@bonliva-crm/graphql";
import { yupResolver } from "@hookform/resolvers/yup";
import { useLocale } from "@hooks";
import { OpportunityFormData, formEditSchema } from "./utils";
import { ConsultantOpportunityForm } from "./consultant-opportunity-form";
import CircularProgress from "@material-ui/core/CircularProgress";

type Props = {
  open: boolean;
  issueId: string;
  onClose: () => void;
};

export function EditOpportunityFormModal(props: Props) {
  const { showError, showSuccess } = useSnackbarsPipeline();
  const { locale } = useLocale();

  const form = useForm<OpportunityFormData>({
    resolver: yupResolver(formEditSchema)
  });

  const [fetchOpportunity, opportunityQuery] = useGetConsultantOpportunityCreateLazyQuery(
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        const issue = data.consultantOpportunityById;

        form.reset({
          consultantId: issue.consultant._id,
          areaOfExpertise: issue.areaOfExpertise
            ? {
                label: issue.areaOfExpertise,
                value: issue.areaOfExpertise
              }
            : null,
          weeks: issue.weeks.map((x) => new Date(x)),
          estimatedResponseDate: issue.dueDate ? new Date(issue.dueDate) : null,
          note: issue.description,
          costCenter: issue.costCenter
            ? {
                label: issue.costCenter.Name,
                value: issue.costCenter.Id
              }
            : null,
          customer: issue.customer as any
        });
      }
    }
  );

  const [updateOpportunity, updateOpportunityResult] =
    useEditConsultantOpportunityMutation({
      onCompleted: () => {
        showSuccess("Presentation card updated");
        props.onClose();
      },
      onError: (e) => {
        showError("Failed to updated presentation card");
        console.error(e);
      }
    });

  useEffect(() => {
    if (props.open) {
      fetchOpportunity({
        variables: {
          id: props.issueId
        }
      });
    }
  }, [props.open, fetchOpportunity]);

  const submit = () => {
    const values = form.getValues();

    if (!values) {
      return;
    }

    return updateOpportunity({
      variables: {
        issueId: props.issueId,
        input: {
          customerId: values.customer._id,
          note: values.note ?? "",
          areaOfExpertise: values.areaOfExpertise?.value ?? null,
          costCenter: values.costCenter?.value ?? null,
          estimatedResponseDate: values.estimatedResponseDate
        }
      }
    });
  };

  return (
    <FormProvider {...form}>
      <Dialog open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Skapa presentation</DialogTitle>

        <DialogContent>
          {opportunityQuery.data && (
            <ConsultantOpportunityForm
              consultantId={
                opportunityQuery.data.consultantOpportunityById.consultant._id
              }
              availablityCalendarDisabled
              withUsersSelector={false}
            />
          )}

          {opportunityQuery.loading && (
            <Box display="flex" justifyContent="center" mt={2} minHeight={100}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={props.onClose}>{locale.Cancel}</Button>
          <Button
            onClick={() => submit()}
            disabled={
              !form.formState.isValid ||
              updateOpportunityResult.loading ||
              !form.formState.isDirty
            }
          >
            {locale.Save}
          </Button>
        </DialogActions>
      </Dialog>

      <DevTool control={form.control} />
    </FormProvider>
  );
}
